import React, { useState } from 'react';
import { AlertCircle, ChevronRight, X } from 'lucide-react';
import { useActiveAnnouncements } from '../hooks/useAnnouncements';
import { AnnouncementDetailModal } from './AnnouncementDetailModal';
import { copy } from '../copy/copy';
import { Bilingual } from './Bilingual';
import { useLanguage } from '../lib/LanguageContext';
import type { Announcement } from '@lms/shared/types';

export function AnnouncementBanner() {
  const { data: activeAnnouncements } = useActiveAnnouncements({
    targetAudience: 'ADMIN_ONLY',
  });
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null);
  const [dismissedIds, setDismissedIds] = useState<string[]>(() => {
    try {
      return JSON.parse(sessionStorage.getItem('dismissed_announcements') || '[]');
    } catch {
      return [];
    }
  });

  const { t } = useLanguage();

  // Find the first urgent announcement that is not dismissed
  const urgentAnnouncement = Array.isArray(activeAnnouncements)
    ? activeAnnouncements.find(
        a => a && a.id && a.priority === 'URGENT' && !dismissedIds.includes(a.id)
      )
    : null;

  if (!urgentAnnouncement) return null;

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!urgentAnnouncement?.id) return;
    const updated = [...dismissedIds, urgentAnnouncement.id];
    setDismissedIds(updated);
    try {
      sessionStorage.setItem('dismissed_announcements', JSON.stringify(updated));
    } catch {
      // ignore
    }
  };

  return (
    <>
      <div className="bg-error-container text-on-error-container px-4 py-2.5 sm:px-8 flex items-center justify-between gap-4 shadow-sm animate-fadeIn">
        <div
          onClick={() => setSelectedAnnouncement(urgentAnnouncement)}
          className="flex items-center gap-3 cursor-pointer flex-1 min-w-0 group"
        >
          <span className="shrink-0 p-1 bg-error/20 text-on-error-container rounded-full animate-pulse">
            <AlertCircle size={16} />
          </span>
          <div className="flex items-center gap-2 min-w-0 text-xs sm:text-sm font-body font-bold">
            <span className="shrink-0 uppercase tracking-wider text-[10px] font-label bg-on-error-container text-error-container px-1.5 py-0.5 rounded font-extrabold">
              <Bilingual {...copy.announcements.urgentNotice} />
            </span>
            <span className="truncate group-hover:underline">{urgentAnnouncement.title}</span>
          </div>
          <ChevronRight
            size={16}
            className="shrink-0 opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all rtl:rotate-180"
          />
        </div>

        <button
          type="button"
          onClick={handleDismiss}
          aria-label={t(copy.announcements.dismissNotice)}
          className="shrink-0 p-1 hover:bg-error/20 rounded-full transition-colors text-on-error-container/80 hover:text-on-error-container focus:outline-none"
        >
          <X size={16} />
        </button>
      </div>

      <AnnouncementDetailModal
        announcement={selectedAnnouncement}
        isOpen={!!selectedAnnouncement}
        onClose={() => setSelectedAnnouncement(null)}
      />
    </>
  );
}
